/**
 * Activity Store — manajemen state aktivitas inti EKINKINIAN.
 *
 * Menggunakan Svelte 5 runes ($state) di module scope (.svelte.ts).
 * Tanggung jawab:
 * - Memuat aktivitas untuk workspace aktif dari SQLite cache.
 * - Search & filter reaktif (UC-05, FR-ACT-02, FR-ACT-06).
 * - CRUD aktivitas (UC-03, UC-04) + tag sync (FR-ACT-05).
 *
 * Acuan: docs/02-UseCase.md UC-03/04/05, docs/03-Workflow.md WF-03
 *         docs/01-SRS.md FR-ACT-01..07
 */
import {
  ActivityRepository,
  TagRepository,
  type CreateActivityInput,
  type UpdateActivityInput,
} from './db';
import type { ActivityCache, ActivityStatus, Priority } from './db';

// ---------------------------------------------------------------------------
// Repository singletons
// ---------------------------------------------------------------------------
const activityRepo = new ActivityRepository();
const tagRepo = new TagRepository();

// ---------------------------------------------------------------------------
// Types — Filter state
// ---------------------------------------------------------------------------

/** State filter untuk daftar aktivitas (UC-05). */
export interface ActivityFilters {
  searchQuery: string;
  status: ActivityStatus | null;
  categoryId: string | null;
  dateFrom: string | null;
  dateTo: string | null;
}

/** Data form aktivitas yang dikirim dari ActivityForm ke store. */
export interface ActivityFormData {
  title: string;
  description: string | null;
  activity_date: string;
  start_time: string | null;
  end_time: string | null;
  duration_minutes: number | null;
  location: string | null;
  category_id: string | null;
  status: ActivityStatus;
  priority: Priority;
}

/** Filter kosong (untuk reset). */
const EMPTY_FILTERS: ActivityFilters = {
  searchQuery: '',
  status: null,
  categoryId: null,
  dateFrom: null,
  dateTo: null,
};

// ---------------------------------------------------------------------------
// Reactive state (module-level, Svelte 5 runes)
// ---------------------------------------------------------------------------
let _activities = $state<ActivityCache[]>([]);
let _loading = $state(false);
let _error = $state<string | null>(null);
let _filters = $state<ActivityFilters>({ ...EMPTY_FILTERS });

/** Workspace + tenant ID untuk store ini. Null = belum di-init. */
let _initializedWorkspaceId: string | null = null;
let _tenantId: string | null = null;

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

/**
 * Inisialisasi activity store untuk workspace tertentu.
 *
 * @param workspaceId ID workspace aktif.
 * @param tenantId ID tenant (pseudo = userId sampai S14).
 */
export async function initActivities(workspaceId: string, tenantId: string): Promise<void> {
  if (_initializedWorkspaceId === workspaceId) return;
  _initializedWorkspaceId = workspaceId;
  _tenantId = tenantId;

  _loading = true;
  _error = null;

  try {
    _activities = await activityRepo.findByWorkspace(workspaceId);
  } catch (err) {
    _error = err instanceof Error ? err.message : 'Gagal memuat aktivitas';
    console.error('[activity] Init failed:', err);
  } finally {
    _loading = false;
  }
}

// ---------------------------------------------------------------------------
// CRUD Operations
// ---------------------------------------------------------------------------

/**
 * Buat aktivitas baru (UC-03, FR-ACT-01).
 *
 * @param data Input aktivitas (judul wajib, tanggal wajib).
 * @param tagIds Optional: array ID tag untuk aktivitas ini (FR-ACT-05).
 * @returns Aktivitas yang dibuat, atau null jika gagal.
 */
export async function createActivity(
  data: Omit<CreateActivityInput, 'tenant_id' | 'workspace_id' | 'created_by'>,
  tagIds: string[] = [],
): Promise<ActivityCache | null> {
  const workspaceId = _initializedWorkspaceId;
  const tenantId = _tenantId;
  if (!workspaceId || !tenantId) {
    _error = 'Belum ada workspace aktif — tidak dapat membuat aktivitas';
    return null;
  }

  try {
    const created = await activityRepo.create({
      ...data,
      tenant_id: tenantId,
      workspace_id: workspaceId,
      created_by: tenantId, // pseudo: user ID sebagai created_by
    });

    // Sync tags jika ada (FR-ACT-05)
    if (tagIds.length > 0) {
      await tagRepo.setTagsForActivity(created.id, tagIds);
    }

    _activities = [created, ..._activities];
    return created;
  } catch (err) {
    _error = err instanceof Error ? err.message : 'Gagal membuat aktivitas';
    console.error('[activity] Create failed:', err);
    return null;
  }
}

/**
 * Perbarui aktivitas yang ada (UC-04, FR-ACT-03).
 *
 * @param id ID aktivitas.
 * @param changes Perubahan field.
 * @param tagIds Optional: array ID tag baru (akan replace tag yang ada).
 * @returns Aktivitas yang diperbarui, atau null jika gagal.
 */
export async function updateActivity(
  id: string,
  changes: UpdateActivityInput,
  tagIds?: string[],
): Promise<ActivityCache | null> {
  try {
    const updated = await activityRepo.update(id, changes);
    if (updated) {
      _activities = _activities.map((a) => (a.id === id ? updated : a));

      // Sync tags jika parameter diberikan (FR-ACT-05)
      if (tagIds !== undefined) {
        await tagRepo.setTagsForActivity(id, tagIds);
      }
    }
    return updated;
  } catch (err) {
    _error = err instanceof Error ? err.message : 'Gagal memperbarui aktivitas';
    console.error('[activity] Update failed:', err);
    return null;
  }
}

/**
 * Soft delete aktivitas (UC-04 A2, FR-ACT-04).
 * Aktivitas disembunyikan dari daftar default tapi tetap di DB.
 */
export async function deleteActivity(id: string): Promise<void> {
  try {
    await activityRepo.softDelete(id);
    _activities = _activities.filter((a) => a.id !== id);
  } catch (err) {
    _error = err instanceof Error ? err.message : 'Gagal menghapus aktivitas';
    console.error('[activity] Delete failed:', err);
  }
}

/**
 * Restore aktivitas dari trash (UC-04 A3).
 * Aktivitas dikembalikan ke daftar aktif.
 */
export async function restoreActivity(id: string): Promise<void> {
  try {
    await activityRepo.restore(id);
    // Reload untuk mendapatkan data yang sudah di-restore
    const restored = await activityRepo.findById(id);
    if (restored) {
      _activities = [restored, ..._activities];
    }
  } catch (err) {
    _error = err instanceof Error ? err.message : 'Gagal memulihkan aktivitas';
    console.error('[activity] Restore failed:', err);
  }
}

// ---------------------------------------------------------------------------
// Search & Filter (UC-05, FR-ACT-02, FR-ACT-06)
// ---------------------------------------------------------------------------

/** Set kata kunci pencarian (FR-ACT-06). */
export function setSearchQuery(query: string): void {
  _filters = { ..._filters, searchQuery: query };
}

/** Set filter status (FR-ACT-02). */
export function setFilterStatus(status: ActivityStatus | null): void {
  _filters = { ..._filters, status };
}

/** Set filter kategori (FR-ACT-02). */
export function setFilterCategory(categoryId: string | null): void {
  _filters = { ..._filters, categoryId };
}

/** Set filter rentang tanggal (FR-ACT-02). */
export function setFilterDateRange(from: string | null, to: string | null): void {
  _filters = { ..._filters, dateFrom: from, dateTo: to };
}

/** Reset semua filter ke kondisi kosong. */
export function clearFilters(): void {
  _filters = { ...EMPTY_FILTERS };
}

// ---------------------------------------------------------------------------
// Refresh & Reset
// ---------------------------------------------------------------------------

/** Muat ulang daftar aktivitas dari cache. */
export async function refreshActivities(): Promise<void> {
  const workspaceId = _initializedWorkspaceId;
  if (!workspaceId) return;

  try {
    _activities = await activityRepo.findByWorkspace(workspaceId);
  } catch (err) {
    _error = err instanceof Error ? err.message : 'Gagal memuat ulang aktivitas';
    console.error('[activity] Refresh failed:', err);
  }
}

/** Reset state aktivitas (dipanggil saat logout atau workspace switch). */
export function resetActivities(): void {
  _activities = [];
  _loading = false;
  _error = null;
  _filters = { ...EMPTY_FILTERS };
  _initializedWorkspaceId = null;
  _tenantId = null;
}

/** Hapus pesan error saat ini. */
export function clearActivityError(): void {
  _error = null;
}

// ---------------------------------------------------------------------------
// Reactive Accessors
// ---------------------------------------------------------------------------

/**
 * Akses reaktif ke state aktivitas.
 * `filteredActivities` menggabungkan search + filter secara client-side.
 */
export const activity = {
  /** Semua aktivitas (tanpa filter). */
  get all(): ActivityCache[] {
    return _activities;
  },
  /**
   * Aktivitas setelah diterapkan search + filter.
   * Filtering dilakukan client-side karena data sudah di-load ke memori
   * (offline-first, jumlah aktivitas per workspace relatif kecil).
   */
  get filtered(): ActivityCache[] {
    let result = _activities;

    // Search filter (FR-ACT-06)
    if (_filters.searchQuery.trim()) {
      const q = _filters.searchQuery.toLowerCase();
      result = result.filter(
        (a) =>
          a.title.toLowerCase().includes(q) || (a.description?.toLowerCase().includes(q) ?? false),
      );
    }

    // Status filter (FR-ACT-02)
    if (_filters.status) {
      result = result.filter((a) => a.status === _filters.status);
    }

    // Category filter (FR-ACT-02)
    if (_filters.categoryId) {
      result = result.filter((a) => a.category_id === _filters.categoryId);
    }

    // Date range filter (FR-ACT-02)
    if (_filters.dateFrom) {
      result = result.filter((a) => a.activity_date >= _filters.dateFrom!);
    }
    if (_filters.dateTo) {
      result = result.filter((a) => a.activity_date <= _filters.dateTo!);
    }

    return result;
  },
  /** Cari aktivitas berdasarkan ID. */
  getById(id: string): ActivityCache | null {
    return _activities.find((a) => a.id === id) ?? null;
  },
  /** State filter saat ini. */
  get filters(): ActivityFilters {
    return _filters;
  },
  /** True jika ada filter aktif. */
  get hasActiveFilters(): boolean {
    return (
      _filters.searchQuery.trim() !== '' ||
      _filters.status !== null ||
      _filters.categoryId !== null ||
      _filters.dateFrom !== null ||
      _filters.dateTo !== null
    );
  },
  /** True jika sedang memuat. */
  get loading(): boolean {
    return _loading;
  },
  /** Pesan error terakhir, atau null. */
  get error(): string | null {
    return _error;
  },
  /** Jumlah total aktivitas (tanpa filter). */
  get count(): number {
    return _activities.length;
  },
  /** Jumlah aktivitas hasil filter. */
  get filteredCount(): number {
    return this.filtered.length;
  },
  /** True jika store sudah di-init untuk suatu workspace. */
  get isInitialized(): boolean {
    return _initializedWorkspaceId !== null;
  },
};
